import React from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from 'react-i18next';
import Styles from '../../styles/minisite/minisite.module.css';
import ColorsOptions from './leftMenu/colorsOptions';
import SecurityOptions from './leftMenu/securityOptions';
import { Tooltip, Icon, Button, Dropdown } from '@weezevent/nacre';
import OtherOptions from './leftMenu/otherOptions';
import { useForm } from '../../utils/validators';
import ConfirmModal from '../../components/confirmModal';
import MinisiteStatus from './minisiteStatus';

function MenuItem({ openOption, name, toggleOption, icon, title, children }) {
    return (
        <div className={Styles['option-container']}>
            <div className={`${Styles['section-header']} ${openOption === name ? Styles['active'] : ''}`} onClick={() => toggleOption(name)}>
                <Icon name={openOption === name ? 'chevron-bottom' : 'chevron-right'} size="small" />
                <Icon className={Styles['icon-header']} name={icon} />
                <span>{title}</span>
            </div>
            {children}
        </div>
    );
}

MenuItem.propTypes = {
    openOption: PropTypes.string,
    name: PropTypes.string,
    toggleOption: PropTypes.func.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    children: PropTypes.node
};

function StatusCtaButton({ minisite, statusMinisite, onSelectSchedule, event }) {
    const { t } = useTranslation();
    const [isPublishing, setIsPublishing] = React.useState(false);

    const setStatus = React.useCallback(
        (e, status) => {
            setIsPublishing(true);
            if (status !== statusMinisite) {
                onSelectSchedule(status);
            }
        },
        [onSelectSchedule, statusMinisite]
    );

    const options = React.useMemo(() => {
        return [
            {
                text: t('ticket.event.minisite.other_options.status.event'),
                value: 'event',
                disabled: event.status === 'published'
            },
            {
                text: t('ticket.event.minisite.other_options.status.scheduled'),
                value: 'scheduled'
            }
        ];
    }, [event]);

    return (
        <div className={statusMinisite !== 'now' ? Styles['status-container'] : ''}>
            <Button
                className={Styles['status-cta-button']}
                {...(!minisite.is_published && { primary: true })}
                onClick={e => setStatus(e, minisite.is_published ? 'offline' : 'now')}
                isLoading={isPublishing}
            >
                {minisite.is_published ? t('ticket.event.minisite.cta-draft') : t('ticket.event.minisite.cta-publish')}
            </Button>
            {!minisite.is_published && (
                <div className={Styles['dropdown-container']}>
                    <Dropdown>
                        {options.map(option => {
                            return (
                                <Dropdown.Item key={option.value} disabled={option.disabled} onClick={e => setStatus(e, option.value)}>
                                    <div className={statusMinisite === option.value ? Styles['dropdown-item-active'] : Styles['dropdown-item']}>
                                        {t(`ticket.event.minisite.other_options.status.${option.value}`)}
                                        {option.value === statusMinisite && <Icon name="check" />}
                                    </div>
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown>
                </div>
            )}
        </div>
    );
}
export default function EditMenu({
    organization,
    event,
    standalone,
    toggleMenu,
    showMenu,
    handleSubmit,
    handleDelete,
    minisite,
    initialValues,
    onSelectSchedule,
    submitSuccess,
    isLoading,
    createBlockEventRelated,
    hasTranslationCapability
}) {
    const { t } = useTranslation();

    //we want to open one of options where we click, it can be color/security/others,
    //ex. openOption is null but if we click on item couleurs et police openOption will be set to 'color'
    const [openOption, setOpenOption] = React.useState('color');
    const [confirmBack, setConfirmBack] = React.useState(false);

    const toggleOption = React.useCallback(
        value => {
            setOpenOption(openOption === value ? null : value);
        },
        [openOption]
    );

    const [formMinisite, setFormMinisite, setFormMinisiteObject] = useForm(initialValues);

    const handleChange = React.useCallback(
        (name, value, update = true) => {
            if (update) {
                if (name === 'language_dropdown') {
                    return createBlockEventRelated('language-dropdown', t('ticket.event.minisite.block.multiLangSuccess'));
                }
                if (name === 'social_img' && value?.image === 'event-banner') {
                    return handleSubmit('event_social', null);
                }
                handleSubmit(name, value);
            }
            if (name === 'social_img') {
                if (value.image === 'event-banner') {
                    setFormMinisite(name, event.full_picture);
                } else {
                    setFormMinisite(name, value.image);
                }
            } else if (name === 'reset_colors') {
                setFormMinisiteObject(value, true);
            } else {
                setFormMinisite(name, value);
            }
        },
        [initialValues, handleSubmit, setFormMinisite, setFormMinisiteObject]
    );

    const handleResetValue = React.useCallback(name => {
        setFormMinisite(name, initialValues[name]);
    });

    const _handleDelete = React.useCallback(
        name => {
            setFormMinisite(name, null);
            handleDelete(name);
            if (name === 'social_img') {
                handleDelete('event_social');
            }
        },
        [handleSubmit]
    );

    const status = React.useMemo(() => {
        if (minisite.delivery && minisite.delivery.on_publish && minisite.delivery.on_publish.length > 0) {
            return 'event';
        }
        if (minisite.delivery && minisite.delivery.on_date) {
            return 'scheduled';
        }
        if (minisite.is_published) {
            return 'now';
        }
        return 'offline';
    }, [minisite]);

    return (
        <div className={`minisite ${Styles['container']}`} style={{ margin: standalone ? 0 : null, left: showMenu ? 0 : '-600px', width: standalone ? '100%' : '311px' }}>
            {standalone ? (
                <div className={Styles['back-to-orga']}>
                    <Tooltip title={<Trans i18nKey="ticket.event.minisite.header.back-to-weezticket-tooltip" />} triggerOnHover margin={false} position="bottom">
                        <Button
                            fluid
                            primary
                            inverted
                            className={Styles['backOrga']}
                            onClick={() =>
                                minisite.is_published && !minisite.is_live
                                    ? setConfirmBack(true)
                                    : (window.location = `${window.weezConfig.temporary.menhirUrl}/bo/tableau_bord_accueil.php?id_evenement=${event.id}`)
                            }
                        >
                            <Icon name="arrow-left" circular inverted margin={false} /> <Trans i18nKey="ticket.common.action.back" />
                        </Button>

                        {confirmBack && (
                            <ConfirmModal
                                labelButton={t('ticket.event.minisite.backModal.confirm')}
                                labelTitle={t('ticket.event.minisite.backModal.title')}
                                labelBody={<Trans i18nKey="ticket.event.minisite.backModal.content" />}
                                objectToSave={formMinisite}
                                onClose={() => setConfirmBack(false)}
                                onAccept={() => (window.location = `${window.weezConfig.temporary.menhirUrl}/bo/tableau_bord_accueil.php?id_evenement=${event.id}`)}
                            />
                        )}
                    </Tooltip>
                </div>
            ) : (
                <div className={Styles['close']} onClick={toggleMenu}>
                    <Icon name="cross" size="small" />
                    <span>
                        <Trans i18nKey="ticket.common.action.close" />
                    </span>
                </div>
            )}
            <MinisiteStatus site={minisite} event={event} onScheduled={() => onSelectSchedule('scheduled')} onConversion={() => onSelectSchedule('event')} />
            <StatusCtaButton event={event} minisite={minisite} statusMinisite={status} onSelectSchedule={onSelectSchedule} />
            <MenuItem openOption={openOption} name="color" toggleOption={toggleOption} icon="color-fill" title={<Trans i18nKey="ticket.event.minisite.colors_and_fonts" />}>
                {openOption === 'color' && <ColorsOptions organization={organization} formMinisite={formMinisite} handleChange={handleChange} handleDelete={_handleDelete} />}
            </MenuItem>
            <div className={Styles['line']} />
            <MenuItem openOption={openOption} name="security" toggleOption={toggleOption} icon="link-bis" title={<Trans i18nKey="ticket.event.minisite.address_and_security" />}>
                {openOption === 'security' && (
                    <SecurityOptions
                        initialValues={initialValues}
                        organization={organization}
                        formMinisite={formMinisite}
                        handleChange={handleChange}
                        resetValue={handleResetValue}
                        submitSuccess={submitSuccess}
                        isLoading={isLoading}
                    />
                )}
            </MenuItem>
            <div className={Styles['line']} />
            <MenuItem openOption={openOption} name="other" toggleOption={toggleOption} icon="cog" title={<Trans i18nKey="ticket.event.minisite.other_options_item" />}>
                {openOption === 'other' && (
                    <OtherOptions
                        organization={organization}
                        event={event}
                        formMinisite={formMinisite}
                        handleChange={handleChange}
                        handleDelete={_handleDelete}
                        hasTranslationCapability={hasTranslationCapability}
                    />
                )}
            </MenuItem>
        </div>
    );
}

EditMenu.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    toggleMenu: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleDelete: PropTypes.func.isRequired,
    showMenu: PropTypes.bool.isRequired,
    initialValues: PropTypes.object,
    standalone: PropTypes.bool.isRequired,
    createBlockEventRelated: PropTypes.func.isRequired,
    hasTranslationCapability: PropTypes.bool.isRequired
};
